import { useState } from 'react'

import { useSetIsIncidentReal } from '../../../../shared/hooks/useSetIsIncidentReal'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { startTimer, trackEvent } from '@/utils/analytics/useAnalytics'
import { useTranslation } from 'react-i18next'

import { useApolloClient } from '@apollo/client'

import { Box, Button, MultilineTextInput, useDisclosure } from '@hivewatch/dark'

import {
  IncidentDocument,
  IncidentQueryResult,
  useCreateIncidentActionMutation,
} from '@/graphql/generated/hooks'
import { IncidentActionType, IncidentStatus } from '@/graphql/generated/schemas'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { BUTTON_PRESS } from '@/utils/analytics'
import { includeIfActive } from '@/utils/apollo'
import { formatFullName } from '@/utils/formats'

import { MixpanelDataIProps } from '../../../../shared/types/types'
import { TakeOverIncidentModal } from '../../../modals/TakeOverIncidentModal'
import { ResolveToggle } from './ResolveToggle'

type ColorScheme = 'dark' | 'light'
type IncidentDataIProps = IncidentQueryResult['data']['incident']

interface ResolveIProps {
  incident?: IncidentDataIProps
  mixpanelData?: MixpanelDataIProps
  onActionComplete?: () => void
  colorScheme?: ColorScheme
}

export const Resolve = ({
  incident,
  mixpanelData,
  onActionComplete,
  colorScheme = 'dark',
}: ResolveIProps) => {
  const { t } = useTranslation()
  const { shouldEnableIncidentActions } = usePermissions()
  const status = incident?.status
  const incidentId = incident?.id
  const ownerId = incident?.owner?.id

  const [createIncidentAction, { loading: isLoading }] =
    useCreateIncidentActionMutation()
  const client = useApolloClient()
  const [isFormDisabled, setIsFormDisabled] = useState(
    status === IncidentStatus.Active || status === IncidentStatus.Resolved
  )
  const [isReal, setIsReal] = useState(incident?.real)
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const me = useAppSelector(selectMe)
  const { setIncidentReal } = useSetIsIncidentReal()
  const { open: isOpen, onOpen, onClose } = useDisclosure()
  const { showSuccess, showError } = useToasts()

  const shouldTakeOverFirst = ownerId !== me?.id && ownerId !== undefined
  const currentOwner = formatFullName(incident?.owner ?? {}) || 'Unassigned'

  if (!Object.values(IncidentStatus).includes(status)) {
    return null
  }

  const onSubmit = async () => {
    const resolveButtonAnalyticName = `${BUTTON_PRESS} Resolve now - Submit`
    startTimer(resolveButtonAnalyticName)
    setIsSubmitting(true)

    try {
      if (isReal !== incident?.real) {
        await setIncidentReal(incidentId, isReal, false, mixpanelData)
      }

      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.Resolved,
            message: description,
          },
        },
        // No awaitRefetchQueries: a failed refetch must not make an
        // already-committed resolve report as failed (apollo-data-fetching.md).
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
          // Refresh the Priority Queue Next Up/Resume target so a just-resolved
          // incident drops out of it immediately. Included only while that query
          // is mounted (PQ active) to avoid Apollo's "Unknown query" dev warning.
          ...includeIfActive(client, 'NextUpIncident'),
        ],
      })

      onActionComplete?.()
      setIsFormDisabled(true)
      trackEvent(resolveButtonAnalyticName, mixpanelData)
      showSuccess(t('incidents:incident_is_resolved'))
    } catch (e) {
      showError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const trackResolutionDescriptionInputFocus = () => {
    trackEvent(
      `Resolution Description Input Field Focused (Resolve Tab)`,
      mixpanelData
    )
  }

  const onCancel = () => {
    trackEvent(`${BUTTON_PRESS} Resolve - Cancel`, mixpanelData)
    onActionComplete?.()
  }

  const onToggleChange = (real: boolean) => {
    setIsReal(real)
  }

  const isDark = colorScheme === 'dark'
  const labelColor = isDark ? 'text.primary' : 'light.text.primary'
  const subtitleColor = isDark ? 'text.secondary' : 'light.text.secondary'

  return (
    <>
      <Box display='flex' flexDirection='column' gap='16px' px='24px' py='16px'>
        <Box
          color={labelColor}
          fontFamily="'Soehne Kraftig', sans-serif"
          fontSize='20px'
          fontWeight='500'
          lineHeight='28px'
        >
          {t('incidents:incident_drawer_resolve_header')}
        </Box>

        <Box display='flex' flexDirection='column' gap='8px'>
          <Box
            color={labelColor}
            fontFamily="'Soehne Kraftig', sans-serif"
            fontSize='14px'
            fontWeight='500'
            lineHeight='20px'
          >
            {t('incidents:resolve_real_question')}
          </Box>
          <Box
            color={subtitleColor}
            fontFamily="'Soehne', sans-serif"
            fontSize='11px'
            fontStyle='italic'
            lineHeight='16px'
          >
            {t('incidents:resolve_false_note')}
          </Box>
          <ResolveToggle
            colorScheme={colorScheme}
            isLoading={isLoading}
            isReal={isReal}
            onChange={onToggleChange}
          />
        </Box>

        <Box onFocus={trackResolutionDescriptionInputFocus}>
          <MultilineTextInput
            colorScheme={colorScheme}
            hasSideBorders={false}
            label={t('incidents:resolve_description_label')}
            onChange={(e) => setDescription(e.target.value)}
            outerPadding='0'
            placeholder={t('incidents:resolve_description_placeholder')}
            value={description}
          />
        </Box>

        <Box display='flex' gap='8px' justifyContent='flex-end'>
          <Button
            colorScheme={colorScheme}
            data-testid='incidentDrawer_resolve_cancel'
            onClick={onCancel}
            size='md'
            variant='secondary'
          >
            {t('common:cancel')}
          </Button>
          <Button
            colorScheme={colorScheme}
            data-testid='incidentDrawer_resolve_submit'
            disabled={
              isFormDisabled ||
              description === '' ||
              (!shouldTakeOverFirst && !shouldEnableIncidentActions)
            }
            isLoading={isSubmitting}
            onClick={shouldTakeOverFirst ? onOpen : onSubmit}
            size='md'
            variant='primary'
          >
            {shouldTakeOverFirst
              ? t('incidents:take_over_and_resolve')
              : t('incidents:resolve_now')}
          </Button>
        </Box>
      </Box>

      {isOpen && (
        <TakeOverIncidentModal
          currentOwner={currentOwner}
          displayId={incident?.displayId}
          incidentName={incident?.name}
          isLoading={isLoading}
          isOpen={isOpen}
          nextAction={IncidentActionType.Resolved}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      )}
    </>
  )
}
